export interface TermsGroup {
    heading: string;
    content?: string[];
    bullets?: string[];
    footer?: string;
}

export interface TermsSection {
    id: string;
    number: string;
    title: string;

    content?: string[];
    bullets?: string[];
    bullets2?: string[];

    subHeading?: string;
    footer?: string;

    groups?: {
        heading: string;
        content?: string[];
        bullets?: string[];
        footer?: string;
    }[];

    contact?: {
        organization: string;
        legal: string;
        support: string;
        website: string;
    };

    agreement?: {
        title: string;
        description: string;
    };
}

export const termsData: TermsSection[] = [
    {
        id: "definitions",
        number: "1",
        title: "Definitions",
        content: [
            "\"Tullu,\" \"TulluApp,\" \"we,\" \"us,\" or \"our\"",
            "Tullu Technologies LLC, an Arizona limited liability company.",

            "\"Partner App\"",
            "The mobile and web application designed for independent esthetic and medical esthetic providers.",

            "\"Platform\"",
            "The Tullu marketplace and technology services that connect clients with providers.",

            "\"Provider\" or \"you\"",
            "Licensed esthetic or medical esthetic professionals who use the Partner App to offer services.",

            "\"Client\" or \"User\"",
            "Individuals who book services through the Platform.",

            "\"Services\"",
            "Esthetic and medical esthetic services provided by Providers to Clients."
        ]
    },
    {
        id: "eligibility",
        number: "2",
        title: "Acceptance and eligibility",
        content: [
            "By using the Partner App, you represent and warrant that:"
        ],
        bullets: [
            "You are at least 18 years of age;",
            "You hold all necessary licenses, certifications, and credentials required to provide esthetic or medical esthetic services in your jurisdiction;",
            "You have the legal capacity to enter into binding agreements;",
            "All information you provide is accurate, current, and complete;",
            "You will maintain the accuracy of such information."
        ]
    },
    {
        id: "account",
        number: "3",
        title: "Account registration and verification",
        content: [
            "To use the Partner App, you must create an account and provide accurate information including:"
        ],

        bullets: [
            "Full legal name and contact information;",
            "Professional licenses and certifications;",
            "Business information (if applicable);",
            "Payment and tax information;",
            "Professional liability insurance details."
        ],

        subHeading: "You agree to:",

        bullets2: [
            "Keep your login credentials confidential;",
            "Notify us immediately of any unauthorized access;",
            "Accept responsibility for all activities under your account;",
            "Maintain current and accurate professional credentials;",
            "Update your information promptly when changes occur."
        ],

        footer:
            "We reserve the right to verify your credentials and may suspend or terminate accounts that fail verification or provide false information."
    },
    {
        id: "contractor",
        number: "4",
        title: "Independent contractor relationship",

        content: [
            "You acknowledge and agree that:"
        ],

        bullets: [
            "You are an independent contractor, not an employee, agent, or partner of Tullu;",
            "Tullu does not provide esthetic or medical esthetic services;",
            "You retain complete professional autonomy over your services;",
            "You are solely responsible for determining which services to offer, pricing, scheduling, and all clinical decisions;",
            "Tullu does not supervise, direct, or control your professional services;",
            "You are responsible for all licenses, permits, insurance, and compliance with applicable laws;",
            "You are responsible for all taxes on income earned through the Platform."
        ]
    },
    {
        id: "platform",
        number: "5",
        title: "Platform services",

        content: [
            "Tullu provides technology services including:"
        ],

        bullets: [
            "Marketplace exposure to connect you with potential clients;",
            "Booking and scheduling management tools;",
            "Secure messaging and communication features;",
            "Client intake and consent form management;",
            "Payment processing services;",
            "Client management and charting tools;",
            "Profile and portfolio management."
        ],

        footer:
            "The Platform facilitates connections but does not guarantee bookings, revenue, or client outcomes."
    },
    {
        id: "provider-obligations",
        number: "6",
        title: "Provider obligations",

        content: [
            "As a provider, you agree to the following."
        ],

        groups: [
            {
                heading: "Professional standards",
                bullets: [
                    "Maintain all required licenses, certifications, and credentials in good standing;",
                    "Comply with all applicable federal, state, and local laws and regulations;",
                    "Maintain professional liability insurance with adequate coverage;",
                    "Provide services in a professional, safe, and competent manner;",
                    "Obtain appropriate informed consent from clients;",
                    "Maintain proper sanitation and safety standards."
                ]
            },

            {
                heading: "Platform conduct",
                bullets: [
                    "Respond to client inquiries and booking requests in a timely manner;",
                    "Honor confirmed bookings or provide reasonable notice of cancellation;",
                    "Accurately represent your services, qualifications, and pricing;",
                    "Maintain professional communication with clients;",
                    "Complete intake forms and documentation as required;",
                    "Adhere to the Tullu Cancellation Policy (as outlined in Section 7)."
                ]
            },

            {
                heading: "Client data and invitations",
                content: [
                    "You represent and warrant that any client contact information you upload, import, or use to send invitations through the Platform was collected by you in compliance with applicable law, and that you have the consent necessary to contact those individuals.",
                    "Specifically:"
                ],
                bullets: [
                    "You are responsible for having a lawful basis to invite or message your clients;",
                    "You will not use the Platform to contact individuals who have not consented to be contacted by you, or who have asked you to stop;",
                    "You will promptly honor any opt-out or unsubscribe request;",
                    "You agree to indemnify Tullu for any claims, penalties, or damages arising from contacts you import or invitations you send without the required consent."
                ]
            },

            {
                heading: "Prohibition on off-platform transactions",
                content: [
                    "You may not:"
                ],
                bullets: [
                    "Solicit or encourage clients to book services outside the Platform to avoid platform fees;",
                    "Provide your personal contact information to clients for the purpose of booking services directly;",
                    "Accept bookings from clients met through the Platform outside of the Platform;",
                    "Use the Platform to establish direct business relationships that bypass platform fees;",
                    "Encourage clients to pay you directly instead of through the Platform."
                ],
                footer:
                    "Violation of this provision may result in immediate account termination, forfeiture of pending payments, and potential legal action to recover damages and platform fees."
            },

            {
                heading: "Prohibited activities",
                bullets: [
                    "Providing services without proper licensure or credentials;",
                    "Misrepresenting your qualifications or experience;",
                    "Engaging in fraudulent, deceptive, or illegal activities;",
                    "Harassing, threatening, or abusing clients or other providers;",
                    "Sharing or selling client information;",
                    "Attempting to circumvent Platform fees or payment processing;",
                    "Using the Platform for any unlawful purpose."
                ]
            }
        ]
    },
    {
        id: "cancellation",
        number: "7",
        title: "Cancellation policy and booking management",

        content: [],

        groups: [
            {
                heading: "Consent requirements",
                content: [
                    "You agree to enforce the following consent requirements for client bookings:"
                ],
                bullets: [
                    "Mobile bookings: clients must complete consent forms within 10 minutes after you accept a booking. If consent is not completed within this timeframe, you may delay or cancel the booking, and the client reservation may be released.",
                    "Scheduled bookings: clients must complete consent forms at least 1 hour before the scheduled appointment. If consent is not completed by the deadline, you may delay or cancel the appointment according to this policy."
                ]
            },

            {
                heading: "Cancellation policy",
                content: [
                    "The following cancellation policy has been established by Tullu in agreement with providers and applies to all bookings."
                ]
            },

            {
                heading: "A) Mobile (instant pool) cancellations",
                content: [
                    "Client cancellation fees apply as follows:"
                ],
                bullets: [
                    "Before provider accepts: Free",
                    "After acceptance, before en route: Free",
                    "Provider en route: 50% cancellation fee",
                    "Provider has arrived: 100% of service fee",
                    "No-show (10 min grace): 100% of service fee"
                ]
            },

            {
                heading: "B) Scheduled booking cancellations",
                content: [
                    "Client cancellation fees based on notice period before start time:"
                ],
                bullets: [
                    "24+ hours notice: Free",
                    "4–24 hours notice: 50% cancellation fee",
                    "Less than 4 hours notice: 100% cancellation fee",
                    "No-show (10 min grace): 100% of service fee"
                ]
            },

            {
                heading: "Provider cancellations",
                content: [
                    "If you must cancel a confirmed booking:"
                ],
                bullets: [
                    "Provide as much advance notice as possible;",
                    "Cancel through the Partner App to notify the client immediately;",
                    "Excessive cancellations may result in account restrictions or termination;",
                    "Last-minute cancellations (within 6 hours for scheduled bookings) may result in penalties or reduced visibility in search results."
                ]
            },

            {
                heading: "Payment of cancellation fees",
                content: [
                    "When a client cancels according to the policy above and a cancellation fee applies, you will receive the applicable percentage of the service fee minus the standard platform fee.",
                    "Refunds to clients will be processed within 3–5 business days."
                ]
            }
        ]
    },
    {
        id: "fees",
        number: "8",
        title: "Fees and payments",

        content: [],

        groups: [
            {
                heading: "Platform fees",
                content: [
                    "Tullu charges a platform fee for each transaction processed through the Platform. The current fee structure is provided during onboarding and may be updated with reasonable advance notice."
                ]
            },

            {
                heading: "Payment processing",
                content: [
                    "All client payments are processed through our third-party payment gateway. Platform fees are deducted from each payment, and the remaining balance is remitted to you according to our payment schedule."
                ]
            },

            {
                heading: "Subscription fees",
                content: [
                    "Certain advanced features or premium tiers may require subscription fees, which will be clearly disclosed."
                ]
            },

            {
                heading: "Refunds and chargebacks",
                content: [
                    "You are responsible for managing refunds and resolving disputes with clients. If a chargeback occurs, you may be liable for the chargeback amount plus any associated fees."
                ]
            }
        ]
    },
    {
        id: "taxes",
        number: "9",
        title: "Taxes",

        content: [
            "You are solely responsible for:"
        ],

        bullets: [
            "Determining and reporting all applicable taxes on income earned through the Platform;",
            "Paying federal, state, and local income taxes;",
            "Paying self-employment taxes;",
            "Maintaining accurate tax records."
        ],

        footer:
            "Tullu will not withhold taxes on your behalf. You will receive IRS Form 1099 or other applicable tax documentation as required by law."
    },
    {
        id: "hipaa",
        number: "10",
        title: "HIPAA and protected health information",

        content: [
            "If you are a covered entity under HIPAA:"
        ],

        bullets: [
            "You agree to execute a separate Business Associate Agreement (BAA) with Tullu;",
            "You remain solely responsible for HIPAA compliance in your provision of services;",
            "Tullu acts as your Business Associate for certain Platform functions;",
            "You are responsible for obtaining proper authorizations and consents from clients;",
            "You must immediately report any suspected breach of PHI."
        ]
    },
    {
        id: "intellectual-property",
        number: "11",
        title: "Intellectual property",

        content: [],

        groups: [
            {
                heading: "TULLU INTELLECTUAL PROPERTY",
                content: [
                    "The Platform, including all software, designs, trademarks, logos, and content, is owned by Tullu and protected by copyright, trademark, and other intellectual property laws. You receive a limited, non-exclusive, non-transferable license to use the Partner App for its intended purpose."
                ]
            },
            {
                heading: "PROVIDER CONTENT",
                content: [
                    "You retain ownership of content you upload (photos, service descriptions, and similar). By uploading content, you grant Tullu a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content to operate and promote the Platform. You represent that you have the rights to all content you upload and that it does not infringe third-party rights."
                ]
            }
        ]
    },
    {
        id: "privacy",
        number: "12",
        title: "Privacy and data protection",

        content: [
            "Your use of the Partner App is subject to our Privacy Policy, which explains how we collect, use, and protect your information. By using the Partner App, you consent to our data practices as described in the Privacy Policy."
        ]
    },
    {
        id: "liability",
        number: "13",
        title: "Liability and indemnification",

        content: [],

        groups: [
            {
                heading: "YOUR LIABILITY",

                content: [
                    "You are solely responsible for:"
                ],

                bullets: [
                    "All services you provide to clients;",
                    "Any outcomes, complications, or adverse events arising from your services;",
                    "Maintaining adequate professional liability insurance;",
                    "Compliance with all applicable laws and regulations."
                ]
            },
            {
                heading: "INDEMNIFICATION",

                content: [
                    "You agree to indemnify, defend, and hold harmless Tullu, its officers, directors, employees, agents, and affiliates from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:"
                ],

                bullets: [
                    "Your provision of services;",
                    "Your breach of these Terms;",
                    "Your violation of any law or regulation;",
                    "Your violation of any third-party rights;",
                    "Any negligence or willful misconduct on your part."
                ]
            },
            {
                heading: "PLATFORM DISCLAIMER",

                content: [
                    "TULLU PROVIDES THE PLATFORM \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TULLU DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. TULLU DISCLAIMS ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT."
                ]
            },
            {
                heading: "LIMITATION OF LIABILITY",

                content: [
                    "TO THE MAXIMUM EXTENT PERMITTED BY LAW, TULLU SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES. TULLU'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO TULLU IN THE 12 MONTHS PRECEDING THE CLAIM."
                ]
            }
        ]
    },
    {
        id: "insurance",
        number: "14",
        title: "Insurance requirements",

        content: [
            "You must maintain professional liability (malpractice) insurance with:"
        ],

        bullets: [
            "Coverage limits appropriate for the services you provide;",
            "Minimum coverage of at least $1,000,000 per occurrence (or as required by your state);",
            "Coverage from a reputable, licensed insurance provider."
        ],

        footer:
            "You must provide proof of insurance upon request and maintain continuous coverage while using the Platform."
    },
    {
        id: "termination",
        number: "15",
        title: "Termination",

        content: [],

        groups: [
            {
                heading: "BY YOU",

                content: [
                    "You may terminate your account at any time by providing written notice to Tullu."
                ]
            },
            {
                heading: "BY TULLU",

                content: [
                    "We may suspend or terminate your account at any time, with or without cause, including for:"
                ],

                bullets: [
                    "Violation of these Terms;",
                    "Fraudulent or illegal activity;",
                    "Attempting to circumvent the platform or conduct off-platform transactions;",
                    "Failure to maintain required licenses or insurance;",
                    "Complaints or safety concerns from clients;",
                    "Inactivity or non-use of the Platform."
                ]
            },
            {
                heading: "EFFECT OF TERMINATION",

                content: [
                    "Upon termination:"
                ],

                bullets: [
                    "Your access to the Partner App will be revoked;",
                    "Outstanding payments will be processed according to our payment schedule;",
                    "You remain liable for any obligations incurred prior to termination;",
                    "Certain provisions (including liability, indemnification, and dispute resolution) survive termination."
                ]
            }
        ]
    },
    {
        id: "changes",
        number: "16",
        title: "Modifications to terms",

        content: [
            "Tullu reserves the right to modify these Terms at any time. We will provide notice of material changes by:"
        ],

        bullets: [
            "Posting the updated Terms in the Partner App;",
            "Sending notice to your registered email address;",
            "Displaying a notification upon login."
        ],

        footer:
            "Continued use of the Partner App after changes become effective constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using the Partner App."
    },
    {
        id: "disputes",
        number: "17",
        title: "Dispute resolution and arbitration",

        content: [],

        groups: [
            {
                heading: "INFORMAL RESOLUTION",

                content: [
                    "Before filing a claim, you agree to contact Tullu at legal@tulluapp.com to attempt to resolve the dispute informally."
                ]
            },
            {
                heading: "BINDING ARBITRATION",

                content: [
                    "Any dispute arising out of or relating to these Terms or the Platform shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules. The arbitration shall be conducted in Arizona, and judgment on the award may be entered in any court having jurisdiction."
                ]
            },
            {
                heading: "CLASS ACTION WAIVER",

                content: [
                    "You agree to resolve disputes on an individual basis only. You waive any right to participate in a class action, class arbitration, or representative action."
                ]
            },
            {
                heading: "EXCEPTIONS",

                content: [
                    "Either party may seek injunctive relief in court for intellectual property infringement or violation of confidentiality obligations."
                ]
            }
        ]
    },
    {
        id: "law",
        number: "18",
        title: "Governing law and jurisdiction",

        content: [
            "These Terms shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to its conflict of law provisions. Any legal action not subject to arbitration shall be brought exclusively in the state or federal courts located in Arizona, and you consent to the personal jurisdiction of such courts."
        ]
    },
    {
        id: "general",
        number: "19",
        title: "General provisions",

        content: [],

        groups: [
            {
                heading: "ENTIRE AGREEMENT",

                content: [
                    "These Terms, together with the Privacy Policy and any applicable BAA, constitute the entire agreement between you and Tullu regarding the Partner App."
                ]
            },
            {
                heading: "SEVERABILITY",

                content: [
                    "If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect."
                ]
            },
            {
                heading: "WAIVER",

                content: [
                    "No waiver of any term shall be deemed a further or continuing waiver of such term or any other term."
                ]
            },
            {
                heading: "ASSIGNMENT",

                content: [
                    "You may not assign these Terms without our prior written consent. Tullu may assign these Terms without restriction."
                ]
            },
            {
                heading: "FORCE MAJEURE",

                content: [
                    "Tullu shall not be liable for any failure or delay in performance due to circumstances beyond its reasonable control."
                ]
            }
        ]
    },
    {
        id: "contact",
        number: "20",
        title: "Contact information",

        content: [
            "For questions about these Terms, please contact us at:"
        ],

        contact: {
            organization: "Tullu Technologies LLC",
            legal: "legal@tulluapp.com",
            support: "support@tulluapp.com",
            website: "tulluapp.com"
        },

        agreement: {
            title:
                "By using the Tullu Partner App, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.",
            description:
                "Questions about these Terms? Email legal@tulluapp.com."
        }
    },
];